import { AppRouters } from '@/utils/constants/routers';
import {
  ChangeLanguageButton,
  ChangeLanguageButtonContent,
  MobileMenuContainer,
  MobileMenuContent,
  MobileMenuFooter,
  MobileMenuFooterContent,
  MobileMenuItem,
  MobileMenuItemText,
  MobileMenuOverlay,
  SimpleButton,
} from './styles';
import {
  AssignmentOutlined,
  BookOutlined,
  ChatBubbleOutline,
  HelpOutline,
  Language,
  LogoutOutlined,
  ManageAccountsOutlined,
  MapOutlined,
  NewspaperOutlined,
} from '@mui/icons-material';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Avatar, Chip, Divider } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { truncateString } from '@/utils/functions/stringFormat';
import { languages, TranslateButton } from '../../translateButton';
import { BottomSheet } from '@/components/atoms/bottomsheet';
import { useRef, useState } from 'react';
import type { SheetRef } from 'react-modal-sheet';
import { Text } from '@/components/atoms/text';
import { useLanguage } from '@/hooks/useLanguage';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { UserRole } from '@/graphql/types/user';
import { BookIcon } from '@/components/atoms/customIcons/book';

interface ItemsProps {
  icon: React.ReactNode;
  label: string;
  path?: string;
  disabled?: boolean;
  tag?: string;
}

export const MobileMenu = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { setLanguage, currentLanguage } = useLanguage();

  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const ref = useRef<SheetRef>(null);

  const getIconColor = (path: string) => {
    return location.pathname === path ? theme.palette.primary.dark : theme.palette.action.active;
  };

  const navigateFromMarkdown = (markdownLink: string): string | null => {
    const regex = /\[.*?\]\((.*?)\)/;
    const match = markdownLink.match(regex);

    if (match && match[1]) {
      return match[1];
    }

    return null;
  };

  const openMarkdownLink = (markdownLink: string): void => {
    const url = navigateFromMarkdown(markdownLink);

    if (url) {
      window.open(url, '_blank');
    }
  };

  const items: ItemsProps[] = [
    {
      icon: <MapOutlined sx={{ color: getIconColor(AppRouters.home) }} />,
      label: t('menu.menu.territorialDashboard'),
      path: AppRouters.home,
    },
    {
      icon: <AssignmentOutlined sx={{ color: getIconColor(AppRouters.projectAnalysis) }} />,
      label: t('menu.menu.myProjects'),
      path: AppRouters.projectAnalysis,
    },
    {
      icon: <BookOutlined sx={{ color: getIconColor(AppRouters.noExist) }} />,
      label: t('menu.menu.method'),
    },
    {
      icon: <BookIcon sx={{ color: getIconColor(AppRouters.noExist) }} />,
      label: t('menu.menu.user_manual'),
    },
    {
      icon: <NewspaperOutlined sx={{ color: getIconColor(AppRouters.noExist) }} />,
      label: t('menu.menu.blog'),
    },
    {
      icon: <HelpOutline sx={{ color: getIconColor(AppRouters.noExist) }} />,
      label: t('menu.menu.faq'),
    },
  ];

  return (
    <>
      <MobileMenuOverlay />

      <MobileMenuContainer>
        <MobileMenuContent>
          {items.map(({ icon, label, path, tag, disabled }, index) => (
            <MobileMenuItem
              key={index}
              variant="text"
              active={location.pathname === path}
              onClick={() => {
                if (path) {
                  navigate(path);
                } else {
                  openMarkdownLink(label);
                }
              }}
              disabled={disabled}
            >
              {icon}

              <MobileMenuItemText>
                <SafeMarkdown text={label || ''} isMenu />

                {tag && <Chip label={tag} variant="outlined" size="small" disabled={disabled} />}
              </MobileMenuItemText>
            </MobileMenuItem>
          ))}
        </MobileMenuContent>

        <MobileMenuFooter>
          <Divider variant="fullWidth" />

          <MobileMenuFooterContent>
            <TranslateButton onChangeLanguage={() => setOpenBottomSheet(true)} />
          </MobileMenuFooterContent>

          <Divider variant="fullWidth" />

          <MobileMenuFooterContent>
            <SimpleButton variant="text" onClick={() => navigate(AppRouters.profile)}>
              <Avatar src={user?.photoURL || ''} />

              <SafeMarkdown text={truncateString(user?.displayName || '', 20)} />
            </SimpleButton>
          </MobileMenuFooterContent>

          <Divider variant="fullWidth" />

          <MobileMenuFooterContent>
            <MobileMenuItem
              variant="text"
              onClick={() => openMarkdownLink(t('menu.menu.contact'))}
              padding={0}
            >
              <ChatBubbleOutline sx={{ color: theme.palette.action.active }} />

              <MobileMenuItemText>
                <SafeMarkdown text={t('menu.menu.contact')} isMenu />
              </MobileMenuItemText>
            </MobileMenuItem>
          </MobileMenuFooterContent>

          {user?.role == UserRole.ADMIN && (
            <>
              <Divider variant="fullWidth" />

              <MobileMenuFooterContent>
                <SimpleButton variant="text" onClick={() => navigate(AppRouters.admin)}>
                  <ManageAccountsOutlined sx={{ color: theme.palette.action.active }} />

                  <SafeMarkdown text={'Admin'} />
                </SimpleButton>
              </MobileMenuFooterContent>
            </>
          )}

          <Divider variant="fullWidth" />

          <MobileMenuFooterContent>
            <SimpleButton variant="text" onClick={logout}>
              <LogoutOutlined sx={{ color: theme.palette.action.active }} />
              <SafeMarkdown text={t('menu.menu.logout')} />
            </SimpleButton>
          </MobileMenuFooterContent>
        </MobileMenuFooter>
      </MobileMenuContainer>

      <BottomSheet
        isOpen={openBottomSheet}
        onClose={() => setOpenBottomSheet(false)}
        ref={ref}
        snapPoints={[1, 0.25, 0]}
        initialSnap={1}
      >
        <ChangeLanguageButtonContent>
          {Object.entries(languages).map(([key, value], index) => (
            <ChangeLanguageButton
              disabled={key === currentLanguage}
              key={index}
              onClick={() => {
                setLanguage(key as 'pt-BR' | 'en-US');
                setOpenBottomSheet(false);
              }}
            >
              <Language sx={{ color: theme.palette.action.active }} />
              <Text>
                <SafeMarkdown text={value.label || ''} />
              </Text>
            </ChangeLanguageButton>
          ))}
        </ChangeLanguageButtonContent>
      </BottomSheet>
    </>
  );
};
